import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

// API's
import { getTechnicianPayRates } from "@/PayPlans/api/technicians/queries";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

const FlagRate = (props) => {
	const {
		sectionId = "default",
		position = "",
		content = {
			defaultValue: {
				input: "Dollar",
				value: 0,
			},
			isGuaranteedDraw: false,
		},
		disabled = false,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;

	const {
		data: payRateData = [],
		isLoading: isLoadingPayRate,
		error: errorPayRate,
		refetch,
	} = useQuery({
		queryKey: ["technicianPayRates", locationId, position],
		queryFn: async () => {
			const result = await getTechnicianPayRates(locationId, position);
			return result?.rates?.map((item) => ({ value: item, label: `$ ${item.toFixed(2)}` })) || [];
		},
		staleTime: Infinity,
		enabled: false, // Don't fetch data on component mount
	});

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrors = errors?.get("defaultValue");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onDefaultFlatRateUpdate = (updatedRetentionPay) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedRetentionPay);
		}
	};

	// States
	const [defaultFlatRate, setDefaultFlatRate] = useCallbackState(
		{
			defaultValue: {
				input: "Percent",
				value: 0,
			},
		},
		onDefaultFlatRateUpdate
	);

	const update = (updatedDefaultFlatRate) => {
		setDefaultFlatRate(updatedDefaultFlatRate);
		wasUpdated.current = true;
	};

	const handleSelectChange = (param) => {
		handleUpdateInput("defaultValue", "value", param.value);
	};

	const handleButtonClick = () => {
		refetch(); // This will trigger the query
	};

	const handleUpdateInput = (area, field, value) => {
		const updated = { ...defaultFlatRate };
		if (area === "isGuaranteedDraw") {
			updated[area] = value;
		} else if (field === "input") {
			updated[area][field] = value;
			updated[area]["value"] = 0;
		} else {
			updated[area][field] = value ? Number(value) : 0;
		}
		update(updated);
	};

	useMemo(() => {
		if (defaultFlatRate) {
			validate([
				{
					definition: {
						useKey: "defaultValue",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									distinct: 0,
								},
							],
						},
						errorsMap: ["defaultValue"],
						message: "Invalid commission",
						validationType: "SIMPLE",
					},
					section: [defaultFlatRate],
				},
			]);
		}
	}, [defaultFlatRate, validate]);

	useEffect(() => {
		setDefaultFlatRate(
			clone(
				content || {
					defaultValue: {
						input: "Dollar",
						value: 0,
					},
					isGuaranteedDraw: false,
				}
			)
		);

		return () => {
			setDefaultFlatRate({
				defaultValue: {
					input: "Percent",
					value: 0,
				},
			});
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, loading, content, setDefaultFlatRate, clearSectionErrors]);

	const isPayRateData = payRateData?.length;

	return (
		<Stack sx={{ padding: "16px 32px 0px" }}>
			{loading ? (
				<>
					<Stack direction="row" spacing={2} mb={1} justifyContent="flex-end">
						<Skeleton variant="rounded" width={140} height={30} />
					</Stack>
					<Stack direction="row" spacing={2} mb={1}>
						<Skeleton variant="rounded" width={95} height={30} />
						<Skeleton variant="rounded" width={150} height={30} />
						<Skeleton variant="rounded" width={120} height={30} />
					</Stack>
				</>
			) : (
				<>
					<Stack direction={"row"} mb={1} justifyContent="flex-end">
						<CustomizedSwitches
							key={"isGuaranteedDraw"}
							textLeft={true}
							textChecked={"Guaranteed Draw"}
							textUnChecked={"Guaranteed Draw"}
							checked={Boolean(defaultFlatRate?.isGuaranteedDraw)}
							disabled={readOnly || loading}
							onChange={(value) => handleUpdateInput("isGuaranteedDraw", "value", value)}
							tooltip={{
								title: "Guarantee the draw.",
								placement: "left",
								key: "default-commissions-guarantee-draw-tooltip",
							}}
						/>
					</Stack>
					<Stack direction="row" spacing={2}>
						<Stack>
							<InputFormExtension
								data={[
									{
										type: "Dollar",
										max: 999999,
										min: 0,
										decimalsEditable: true,
										decimals: 2,
									},
								]}
								sx={{ width: "95px" }}
								defaultType="Dollar"
								value={defaultFlatRate?.defaultValue?.value}
								error={localErrors?.some?.((error) => error?.defaultValue)}
								disabled={disabled}
								readOnly={readOnly}
								onActionUpdate={(value) => handleUpdateInput("defaultValue", "value", value)}
								onChangeType={(_, input) => handleUpdateInput("defaultValue", "input", input)}
							/>
						</Stack>
						<Stack width={150}>
							<SmartSelector
								selectBy={"value"}
								showBy={"label"}
								options={payRateData}
								inputProps={{
									placeholder: isPayRateData ? "Pay Rates" : "Pay Rates not Loaded",
								}}
								selected={null}
								disabled={!isPayRateData || readOnly}
								loading={isLoadingPayRate}
								error={!!errorPayRate}
								onChangeAction={handleSelectChange}
							/>
						</Stack>
						<Stack>
							<Button
								variant="contained"
								onClick={handleButtonClick}
								className={clsx({
									"h-[30px] !rounded-[6px]": true,
									"!bg-primary hover:!shadow-primary-shadow-50": !readOnly,
									"!bg-disabled": readOnly,
								})}
								sx={{ width: "120px" }}
								disabled={readOnly}
								disableElevation
							>
								<Typography
									className={clsx({
										"select-plan-dialog-assign-btn-text": true,
									})}
								>
									Load Pay Rates
								</Typography>
							</Button>
						</Stack>
					</Stack>

					{Boolean(errorsMessages?.length) && (
						<Stack mt={1}>
							{errorsMessages.map((message, key) => {
								const keyIndex = `error_default_flat_rate_${key}`;
								return <TierAlertError key={keyIndex} text={message} />;
							})}
						</Stack>
					)}
				</>
			)}
		</Stack>
	);
};
FlagRate.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.shape({
		defaultValue: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		isGuaranteedDraw: PropTypes.bool,
	}),
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

export default FlagRate;
